/** What the marker may say about one criterion of a marking guide. */
export const VERDICTS = ["met", "partly", "not_yet"] as const;
export type Verdict = (typeof VERDICTS)[number];

/** The part of a criterion scoring needs; its words live with the guide in the curriculum. */
export interface ScoredCriterion {
  readonly id: string;
  readonly maxPoints: number;
}

export type VerdictMap = Readonly<Record<string, Verdict>>;

/** Band edges on the 0 to 100 section score; proposals, pinned here and nowhere else. */
export const MARKER_BAND_EDGES = [50, 70, 85] as const;
export const MARKER_BANDS = ["emerging", "developing", "secure", "strong"] as const;
export type MarkerBand = (typeof MARKER_BANDS)[number];

/** A score this close to an edge gets a second opinion; two passes this close agree. */
export const BORDER_MARGIN = 5;
/** Two passes this far apart, or one verdict flipping met to not yet, show a range. */
export const DISAGREE_POINTS = 10;

export type MarkerConfidence = "high" | "medium" | "low";

/** Points a verdict earns of a criterion's maximum, in halves so an odd maximum stays exact. */
export const criterionPoints = (verdict: Verdict, maxPoints: number): number =>
  verdict === "met" ? maxPoints : verdict === "partly" ? maxPoints / 2 : 0;

/** The section score the server owns: the model's own numbers are never stored. */
export function scoreFromVerdicts(
  criteria: readonly ScoredCriterion[],
  verdicts: VerdictMap,
): number {
  const total = criteria.reduce((sum, c) => sum + c.maxPoints, 0);
  if (total <= 0) return 0;
  const got = criteria.reduce(
    (sum, c) => sum + criterionPoints(verdicts[c.id] ?? "not_yet", c.maxPoints),
    0,
  );
  return Math.round((100 * got) / total);
}

export function markerBand(score: number): MarkerBand {
  const at = MARKER_BAND_EDGES.filter((edge) => score >= edge).length;
  return MARKER_BANDS[at] ?? "emerging";
}

export function isBorderline(score: number): boolean {
  return MARKER_BAND_EDGES.some((edge) => Math.abs(score - edge) <= BORDER_MARGIN);
}

export interface MarkerPass {
  readonly score: number;
  readonly verdicts: VerdictMap;
}

export interface Reconciled {
  readonly score: number;
  readonly range?: { readonly low: number; readonly high: number };
  readonly confidence: MarkerConfidence;
}

const flips = (a: VerdictMap, b: VerdictMap): boolean =>
  Object.keys(a).some((id) => {
    const x = a[id];
    const y = b[id];
    return (x === "met" && y === "not_yet") || (x === "not_yet" && y === "met");
  });

const sameVerdicts = (a: VerdictMap, b: VerdictMap): boolean =>
  Object.keys(a).every((id) => a[id] === b[id]);

/** Joins the first pass and its second opinion into one stored mark. */
export function reconcilePasses(first: MarkerPass, second: MarkerPass): Reconciled {
  const gap = Math.abs(first.score - second.score);
  const midpoint = Math.floor((first.score + second.score) / 2);
  if (gap >= DISAGREE_POINTS || flips(first.verdicts, second.verdicts)) {
    return {
      score: midpoint,
      range: {
        low: Math.min(first.score, second.score),
        high: Math.max(first.score, second.score),
      },
      confidence: "low",
    };
  }
  if (gap <= BORDER_MARGIN && sameVerdicts(first.verdicts, second.verdicts)) {
    return { score: first.score, confidence: "high" };
  }
  return { score: midpoint, confidence: "medium" };
}

/** A hand-in that never got marked ends after this many days; a marked one stays until its owner deletes it. */
export const MARKER_PENDING_RETENTION_DAYS = 30;
/** The re-mark counters only need the current UTC day, and a day of margin. */
export const MARKER_REMARK_RETENTION_DAYS = 2;
/** The spend counter of a UTC day is kept for a month for the founder's cost readout. */
export const MARKER_SPEND_RETENTION_DAYS = 35;
